import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { T } from "@freightsimple/ui-core/Typography/T";

interface AdditionalOptionsProps {
  onLiveChat: () => void;
  onChangePickupDate: () => void;
  onCancelShipment: () => void;
  onSubmitClaim: () => void;
  onRepeatShipment: () => void;
  onModifyShipment: () => void;
  onHelpCenter: () => void;

  shipment: Shipment;
}

function shouldDisableChangePickupDate(shipment: Shipment): boolean {
  switch (shipment.shipmentState) {
    case ShipmentState.Quoted:
    case ShipmentState.QuoteRequested:
    case ShipmentState.BookingConfirmed:
    case ShipmentState.BookingRequested:
    case ShipmentState.BookingFailed:
    case ShipmentState.OnHold:
      return false;
    case ShipmentState.Cancelled:
    case ShipmentState.Lost:
    case ShipmentState.Delivered:
    case ShipmentState.InTransit:
      return true;
  }
}

function shouldDisableCancelShipment(shipment: Shipment): boolean {
  switch (shipment.shipmentState) {
    case ShipmentState.Quoted:
    case ShipmentState.QuoteRequested:
    case ShipmentState.BookingConfirmed:
    case ShipmentState.BookingRequested:
    case ShipmentState.BookingFailed:
    case ShipmentState.OnHold:
      return false;
    case ShipmentState.Cancelled:
    case ShipmentState.Lost:
    case ShipmentState.Delivered:
    case ShipmentState.InTransit:
      return true;
  }
}

function shouldDisableSubmitClaim(shipment: Shipment): boolean {
  // Disable if shipment already has freight claims (1 or more)
  if (shipment.hasFreightClaims === true) {
    return true;
  }

  switch (shipment.shipmentState) {
    // Maybe something is lost in transit, or is being returned
    case ShipmentState.Delivered:
    case ShipmentState.InTransit:
    case ShipmentState.Lost:
      return false;
    case ShipmentState.Quoted:
    case ShipmentState.QuoteRequested:
    case ShipmentState.BookingConfirmed:
    case ShipmentState.BookingRequested:
    case ShipmentState.BookingFailed:
    case ShipmentState.Cancelled:
    case ShipmentState.OnHold:
      return true;
  }
}

function shouldDisableModifyShipment(shipment: Shipment): boolean {
  switch (shipment.shipmentState) {
    case ShipmentState.BookingConfirmed:
    case ShipmentState.InTransit:
    case ShipmentState.OnHold:
      return false;
    case ShipmentState.Quoted:
    case ShipmentState.QuoteRequested:
    case ShipmentState.BookingRequested:
    case ShipmentState.BookingFailed:
    case ShipmentState.Cancelled:
    case ShipmentState.Lost:
    case ShipmentState.Delivered:
      return true;
  }
}

function ViewShipmentsAdditionalOptions(props: AdditionalOptionsProps) {
  const disabledChangePickupDate = shouldDisableChangePickupDate(
    props.shipment,
  );
  const disabledCancelShipment = shouldDisableCancelShipment(props.shipment);
  const disabledSubmitClaim = shouldDisableSubmitClaim(props.shipment);
  const disabledReconsignShipment = shouldDisableModifyShipment(props.shipment);

  return (
    <div className="flex flex-col items-start">
      <T size="base" weight="normal" color="secondary">
        Additional Options
      </T>
      <div className="h-[16px]" />
      <div className="flex flex-row items-center">
        <Button size="link" onClick={props.onLiveChat}>
          Live Chat &amp; Support
        </Button>
        <div className="w-[32px] shrink-0" />
        <Button
          size="link"
          onClick={props.onChangePickupDate}
          disabled={disabledChangePickupDate}
          disabledMessage="Cannot change pickup date after shipment has been picked up."
        >
          Change Pickup Date / Hours
        </Button>
        <div className="w-[32px] shrink-0" />
        <Button
          size="link"
          onClick={props.onCancelShipment}
          disabled={disabledCancelShipment}
          disabledMessage="Cannot cancel shipment after pickup"
        >
          Cancel Shipment
        </Button>
        <div className="w-[32px] shrink-0" />
        <Button
          size="link"
          onClick={props.onSubmitClaim}
          disabled={disabledSubmitClaim}
          disabledMessage={
            props.shipment.hasFreightClaims === true
              ? "Cannot submit more than one claim per shipment."
              : "Cannot submit a claim until delivery has been confirmed"
          }
        >
          Submit Claim
        </Button>
        <div className="w-[32px] shrink-0" />
        <Button size="link" onClick={props.onRepeatShipment}>
          Repeat Shipment
        </Button>
        <div className="w-[32px] shrink-0" />
        <Button
          size="link"
          onClick={props.onModifyShipment}
          disabled={disabledReconsignShipment}
          disabledMessage="Shipments can only be modified prior to pickup or in-transit"
        >
          Modify Shipment
        </Button>
        <div className="w-[32px] shrink-0" />
        <Button size="link" onClick={props.onHelpCenter}>
          Help Center
        </Button>
      </div>
    </div>
  );
}
export default ViewShipmentsAdditionalOptions;
